import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowRight, 
  Printer, 
  Package, 
  Search, 
  Globe, 
  Palette, 
  ShoppingBag,
  Check,
  Users,
  Zap,
  Shield,
  Clock
} from 'lucide-react';

const HomePage = () => {
  const services = [
    {
      id: 'print-on-demand',
      title: 'Print on Demand',
      description: 'Custom printing solutions with no minimum orders. Perfect for unique, personalized products.',
      icon: Printer,
      color: 'bg-green-500',
    },
    {
      id: 'dropshipping',
      title: 'Dropshipping Fulfillment',
      description: 'Seamless order processing and shipping directly to your customers worldwide.',
      icon: Package,
      color: 'bg-blue-500',
    },
    {
      id: 'product-sourcing',
      title: 'Product Sourcing',
      description: 'Access quality suppliers and products at competitive prices for your store.',
      icon: Search,
      color: 'bg-purple-500',
    },
    {
      id: 'global-warehousing',
      title: 'Global Warehousing',
      description: 'Strategic warehouse locations for faster delivery and reduced shipping costs.',
      icon: Globe,
      color: 'bg-orange-500',
    },
    {
      id: 'custom-branding',
      title: 'Custom Branding',
      description: 'White-label solutions with custom packaging, inserts, and branded experience.',
      icon: Palette,
      color: 'bg-pink-500',
    },
    {
      id: 'tiktok-shop',
      title: 'TikTok Shop Solutions',
      description: 'Specialized fulfillment for TikTok sellers with fast processing times.',
      icon: ShoppingBag,
      color: 'bg-red-500',
    },
  ];

  const features = [
    {
      icon: Zap,
      title: 'Fast Processing',
      description: 'Orders processed within 24-48 hours for quick delivery',
    },
    {
      icon: Shield,
      title: 'Quality Guaranteed',
      description: 'Strict quality control on every product before shipping',
    },
    {
      icon: Users,
      title: 'Dedicated Support',
      description: '24/7 customer support team to assist you anytime',
    },
    {
      icon: Clock,
      title: 'Real-time Tracking',
      description: 'Track orders in real-time with automated updates',
    },
  ];

  const stats = [
    { value: '10M+', label: 'Orders Fulfilled' },
    { value: '50+', label: 'Countries Served' },
    { value: '99.5%', label: 'Delivery Rate' },
    { value: '24/7', label: 'Support Available' },
  ];

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.pexels.com/photos/1267338/pexels-photo-1267338.jpeg?auto=compress&cs=tinysrgb&w=1920"
            alt="Modern warehouse"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/95 via-gray-900/80 to-gray-900/60" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-32">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="mb-6"
            >
              <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-green-500/20 text-green-400 text-sm font-medium border border-green-500/30">
                Your Trusted Fulfillment Partner
              </span>
            </motion.div>
            
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-['Chivo'] font-bold text-white mb-6 leading-tight"
              data-testid="hero-title"
            >
              Your Trusted Path to{' '}
              <span className="text-green-500">Fulfillment Excellence</span>
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-lg md:text-xl text-gray-300 mb-8 leading-relaxed max-w-2xl"
              data-testid="hero-description"
            >
              Comprehensive Print on Demand, Dropshipping, and Warehousing solutions 
              to scale your ecommerce business globally with confidence.
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex flex-wrap gap-4"
            >
              <Link
                to="/contact"
                data-testid="hero-cta-primary"
                className="inline-flex items-center gap-2 bg-green-600 text-white px-8 py-3.5 rounded-full font-semibold hover:bg-green-700 transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5 group"
              >
                Get Started Today
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/services"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 bg-white/10 text-white px-8 py-3.5 rounded-full font-semibold border border-white/20 hover:bg-white/20 transition-all duration-300"
              >
                Explore Services
              </Link>
            </motion.div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 0.5 }}
          className="absolute bottom-10 left-1/2 -translate-x-1/2 hidden md:block"
        >
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center">
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-1.5 h-3 bg-white/50 rounded-full mt-2"
            />
          </div>
        </motion.div>
      </section>

      {/* Stats Section */}
      <section className="bg-green-600 py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-2 md:grid-cols-4 gap-8"
          >
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="text-center"
                data-testid={`stat-${index}`}
              >
                <div className="text-3xl md:text-4xl font-['Chivo'] font-bold text-white mb-1">
                  {stat.value}
                </div>
                <div className="text-green-100 text-sm md:text-base">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 md:py-28 bg-gray-50" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-14"
          >
            <span className="text-green-600 font-semibold text-sm tracking-wider uppercase mb-3 block">
              What We Offer
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-['Chivo'] font-bold text-gray-900 mb-4">
              Our Services
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-base md:text-lg">
              Comprehensive fulfillment solutions tailored to your ecommerce needs
            </p>
          </motion.div>

          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                variants={fadeInUp}
                className="group"
              >
                <Link
                  to={`/services#${service.id}`}
                  data-testid={`service-card-${service.id}`}
                  className="block bg-white rounded-2xl p-8 h-full border border-gray-100 hover:border-green-200 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                >
                  <div className={`w-14 h-14 ${service.color} rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300`}>
                    <service.icon size={26} className="text-white" />
                  </div>
                  <h3 className="text-xl font-['Chivo'] font-bold text-gray-900 mb-3 group-hover:text-green-600 transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed mb-4">
                    {service.description}
                  </p>
                  <span className="inline-flex items-center text-green-600 text-sm font-semibold group-hover:gap-2 transition-all">
                    Learn More
                    <ArrowRight size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 md:py-28 bg-white" data-testid="why-choose-us-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-green-600 font-semibold text-sm tracking-wider uppercase mb-3 block">
                Why Choose Us
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-['Chivo'] font-bold text-gray-900 mb-6">
                Built for Ecommerce Success
              </h2>
              <p className="text-gray-600 text-base md:text-lg mb-8 leading-relaxed">
                We understand the challenges of running an online business. Our comprehensive 
                fulfillment solutions are designed to help you scale efficiently while 
                maintaining quality and customer satisfaction.
              </p>
              
              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                {features.map((feature, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="flex items-start gap-3"
                    data-testid={`feature-${index}`}
                  >
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <feature.icon size={20} className="text-green-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">{feature.title}</h4>
                      <p className="text-sm text-gray-600">{feature.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>

              <Link
                to="/about"
                data-testid="learn-more-button"
                className="inline-flex items-center gap-2 text-green-600 font-semibold hover:gap-3 transition-all"
              >
                Learn More About Us
                <ArrowRight size={18} />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="aspect-square rounded-2xl overflow-hidden">
                <img
                  src="https://images.pexels.com/photos/4483610/pexels-photo-4483610.jpeg?auto=compress&cs=tinysrgb&w=800"
                  alt="Warehouse operations"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Floating Card */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-5 max-w-xs hidden md:block">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center">
                    <Check size={20} className="text-white" />
                  </div>
                  <span className="font-semibold text-gray-900">Trusted by 5,000+ Sellers</span>
                </div>
                <p className="text-sm text-gray-600">
                  Join thousands of successful ecommerce businesses powered by our fulfillment.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-gray-900 relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 noise-overlay" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-['Chivo'] font-bold text-white mb-6">
              Ready to Transform Your{' '}
              <span className="text-green-500">Fulfillment?</span>
            </h2>
            <p className="text-gray-400 text-base md:text-lg mb-10 leading-relaxed">
              Partner with Black Mesa Mercantile and experience the difference. 
              Start scaling your business today with our comprehensive solutions.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/contact"
                data-testid="cta-contact-button"
                className="inline-flex items-center gap-2 bg-green-600 text-white px-8 py-3.5 rounded-full font-semibold hover:bg-green-700 transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5 group"
              >
                Contact Us Today
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/faq"
                data-testid="cta-faq-button"
                className="inline-flex items-center gap-2 bg-white/10 text-white px-8 py-3.5 rounded-full font-semibold border border-white/20 hover:bg-white/20 transition-all duration-300"
              >
                View FAQ
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
